import Interceptor from "./Interceptor";

/**
 * Application API Service
 * Handles CRUD operations for Applications and Services
 * Note: Interceptor already includes /api prefix from REACT_APP_API_BASE
 */

// ===========================================================================
// APPLICATION ENDPOINTS
// ===========================================================================

/**
 * Create a new application
 * @param {Object} data - Application data
 * @returns {Promise<Object>} Created application
 */
export const createApplication = async (data) => {
  const response = await Interceptor.post(`/applications`, data);
  return response.data;
};

/**
 * Get all applications for the current user
 * @param {Object} filters - Filter options
 * @returns {Promise<Object>} Applications list with pagination
 */
export const getApplications = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.deploymentType)
    params.append("deploymentType", filters.deploymentType);
  if (filters.status) params.append("status", filters.status);
  if (filters.environment) params.append("environment", filters.environment);
  if (filters.search) params.append("search", filters.search);
  if (filters.page) params.append("page", filters.page);
  if (filters.limit) params.append("limit", filters.limit);

  const response = await Interceptor.get(`/applications?${params.toString()}`);
  return response.data;
};

/**
 * Get a single application by ID
 * @param {string} appId - Application ID
 * @returns {Promise<Object>} Application details
 */
export const getApplicationById = async (appId) => {
  const response = await Interceptor.get(`/applications/${appId}`);
  return response.data;
};

/**
 * Update an application
 * @param {string} appId - Application ID
 * @param {Object} data - Updated data
 * @returns {Promise<Object>} Updated application
 */
export const updateApplication = async (appId, data) => {
  const response = await Interceptor.put(`/applications/${appId}`, data);
  return response.data;
};

/**
 * Delete an application
 * @param {string} appId - Application ID
 * @returns {Promise<Object>} Deletion result
 */
export const deleteApplication = async (appId) => {
  const response = await Interceptor.delete(`/applications/${appId}`);
  return response.data;
};

/**
 * Deploy all services in an application
 * @param {string} appId - Application ID
 * @returns {Promise<Object>} Deployment result
 */
export const deployApplication = async (appId) => {
  const response = await Interceptor.post(`/applications/${appId}/deploy`);
  return response.data;
};

/**
 * Redeploy application (tear down and recreate containers)
 * @param {string} appId - Application ID
 * @returns {Promise<Object>} Redeploy result
 */
export const redeployApplication = async (appId) => {
  const response = await Interceptor.post(`/applications/${appId}/redeploy`);
  return response.data;
};

// ===========================================================================
// CUSTOM DOMAIN ENDPOINTS
// ===========================================================================

/**
 * Add, update, or remove custom domain for an application
 * @param {string} appId - Application ID
 * @param {Object} data - { customDomain } or { removeCustomDomain: true }
 * @returns {Promise<Object>} Domain configuration result with DNS instructions
 */
export const manageApplicationDomain = async (appId, data) => {
  const response = await Interceptor.put(`/applications/${appId}/domain`, data);
  return response.data;
};

/**
 * Verify DNS configuration for application custom domain
 * @param {string} appId - Application ID
 * @returns {Promise<Object>} DNS verification result
 */
export const verifyApplicationDomain = async (appId) => {
  const response = await Interceptor.get(
    `/applications/${appId}/verify-domain`,
  );
  return response.data;
};

// ===========================================================================
// SERVICE ENDPOINTS
// ===========================================================================

/**
 * Add a service to an application
 * @param {string} appId - Application ID
 * @param {Object} serviceData - Service configuration
 * @returns {Promise<Object>} Created service
 */
export const addService = async (appId, serviceData) => {
  const response = await Interceptor.post(
    `/applications/${appId}/services`,
    serviceData,
  );
  return response.data;
};

/**
 * Update a service
 * @param {string} appId - Application ID
 * @param {string} serviceId - Service ID
 * @param {Object} data - Updated data
 * @returns {Promise<Object>} Updated service
 */
export const updateService = async (appId, serviceId, data) => {
  const response = await Interceptor.put(
    `/applications/${appId}/services/${serviceId}`,
    data,
  );
  return response.data;
};

/**
 * Delete a service
 * @param {string} appId - Application ID
 * @param {string} serviceId - Service ID
 * @returns {Promise<Object>} Deletion result
 */
export const deleteService = async (appId, serviceId) => {
  const response = await Interceptor.delete(
    `/applications/${appId}/services/${serviceId}`,
  );
  return response.data;
};

/**
 * Deploy a service
 * @param {string} appId - Application ID
 * @param {string} serviceId - Service ID
 * @returns {Promise<Object>} Deployment result
 */
export const deployService = async (appId, serviceId) => {
  const response = await Interceptor.post(
    `/applications/${appId}/services/${serviceId}/deploy`,
  );
  return response.data;
};

/**
 * Start a service
 * @param {string} appId - Application ID
 * @param {string} serviceId - Service ID
 * @returns {Promise<Object>} Start result
 */
export const startService = async (appId, serviceId) => {
  const response = await Interceptor.post(
    `/applications/${appId}/services/${serviceId}/start`,
  );
  return response.data;
};

/**
 * Stop a service
 * @param {string} appId - Application ID
 * @param {string} serviceId - Service ID
 * @returns {Promise<Object>} Stop result
 */
export const stopService = async (appId, serviceId) => {
  const response = await Interceptor.post(
    `/applications/${appId}/services/${serviceId}/stop`,
  );
  return response.data;
};

/**
 * Restart a service
 * @param {string} appId - Application ID
 * @param {string} serviceId - Service ID
 * @returns {Promise<Object>} Restart result
 */
export const restartService = async (appId, serviceId) => {
  const response = await Interceptor.post(
    `/applications/${appId}/services/${serviceId}/restart`,
  );
  return response.data;
};

/**
 * Get service logs
 * @param {string} appId - Application ID
 * @param {string} serviceId - Service ID
 * @param {Object} options - Log options (tail, since)
 * @returns {Promise<Object>} Logs result
 */
export const getServiceLogs = async (appId, serviceId, options = {}) => {
  const params = new URLSearchParams();
  if (options.tail) params.append("tail", options.tail);
  if (options.since) params.append("since", options.since);

  const response = await Interceptor.get(
    `/applications/${appId}/services/${serviceId}/logs?${params.toString()}`,
  );
  return response.data;
};

/**
 * Get service deployment history
 * @param {string} appId - Application ID
 * @param {string} serviceId - Service ID
 * @param {Object} pagination - Pagination options
 * @returns {Promise<Object>} Deployment logs with pagination
 */
export const getServiceDeploymentLogs = async (
  appId,
  serviceId,
  pagination = {},
) => {
  const params = new URLSearchParams();
  if (pagination.page) params.append("page", pagination.page);
  if (pagination.limit) params.append("limit", pagination.limit);

  const response = await Interceptor.get(
    `/applications/${appId}/services/${serviceId}/deployment-logs?${params.toString()}`,
  );
  return response.data;
};

// Export all functions
const ApplicationService = {
  // Application operations
  createApplication,
  getApplications,
  getApplicationById,
  updateApplication,
  deleteApplication,
  deployApplication,
  redeployApplication,
  // Domain operations
  manageApplicationDomain,
  verifyApplicationDomain,
  // Service operations
  addService,
  updateService,
  deleteService,
  deployService,
  startService,
  stopService,
  restartService,
  getServiceLogs,
  getServiceDeploymentLogs,
};

export default ApplicationService;
